import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UsersThree, Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const ROLES = ["collector", "editor", "admin"];
const EMPTY = { name: "", email: "", password: "", role: "collector" };

function UserForm({ open, onOpenChange, initial, onSubmit, mode }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(initial || EMPTY); }, [open]);

  const submit = async () => {
    setSaving(true);
    const ok = await onSubmit(form);
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131722] border-white/10 text-slate-200" data-testid={`${mode}-user-dialog`}>
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add user" : "Edit user"}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === "add" ? "Create a new account and assign a role." : "Update this user's details. Leave password blank to keep it unchanged."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Full name</Label>
            <Input data-testid={`${mode}-user-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#0B0E14] border-white/10" placeholder="Jane Doe" />
          </div>
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input data-testid={`${mode}-user-email`} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[#0B0E14] border-white/10" placeholder="user@observatory.org" />
          </div>
          <div>
            <Label className="text-slate-300">{mode === "add" ? "Password" : "New password (optional)"}</Label>
            <Input data-testid={`${mode}-user-password`} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-[#0B0E14] border-white/10" placeholder={mode === "add" ? "min 6 characters" : "leave blank to keep"} />
          </div>
          <div>
            <Label className="text-slate-300">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid={`${mode}-user-role`} className="mt-1.5 bg-[#0B0E14] border-white/10 capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full text-slate-400 hover:bg-white/5">Cancel</Button>
          <Button data-testid={`${mode}-user-submit`} onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-full transition-colors duration-200">
            {saving ? "Saving…" : mode === "add" ? "Create user" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ROLE_CLS = {
  admin: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  editor: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  collector: "bg-slate-500/10 text-slate-300 border-slate-500/20",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}/role`, { role }); toast.success("Role updated"); load(); }
    catch { toast.error("Failed to update role"); }
  };

  const createUser = async (form) => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      load();
      return true;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to create user"); return false; }
  };

  const saveUser = async (form) => {
    try {
      const body = { name: form.name, email: form.email, role: form.role };
      if (form.password) body.password = form.password;
      await api.put(`/users/${editUser.id}`, body);
      toast.success("User updated");
      load();
      return true;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to update user"); return false; }
  };

  const removeUser = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete user"); }
  };

  return (
    <div className="h-full overflow-y-auto">
      <header className="h-16 flex items-center justify-between px-8 border-b border-white/5">
        <div className="flex items-center gap-2">
          <UsersThree size={22} weight="duotone" className="text-purple-400" />
          <h1 className="text-xl font-bold tracking-tight">Users</h1>
        </div>
        <Button data-testid="add-user-btn" onClick={() => setAddOpen(true)} className="gap-2 bg-blue-600 hover:bg-blue-500 rounded-full transition-colors duration-200">
          <Plus size={16} weight="bold" /> Add User
        </Button>
      </header>

      <div className="p-8 max-w-3xl">
        <div className="bg-[#131722] border border-white/5 rounded-lg divide-y divide-white/5">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center justify-between px-5 py-4 gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center font-medium shrink-0">{u.name?.[0]?.toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {u.name}
                    {u.id === me?.id && <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">(you)</span>}
                  </div>
                  <div className="font-mono text-xs text-slate-500 truncate">{u.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                  <SelectTrigger data-testid={`user-role-${u.id}`} className={`w-32 h-8 text-xs border ${ROLE_CLS[u.role]}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="icon" variant="ghost" data-testid={`user-edit-${u.id}`} onClick={() => setEditUser(u)} className="h-8 w-8 text-slate-400 hover:text-blue-300 hover:bg-white/5">
                  <PencilSimple size={16} weight="duotone" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid={`user-delete-${u.id}`} disabled={u.id === me?.id}
                      className="h-8 w-8 text-slate-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:pointer-events-none">
                      <Trash size={16} weight="duotone" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#131722] border-white/10 text-slate-200">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">This permanently removes the account for {u.email}. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`user-delete-confirm-${u.id}`} onClick={() => removeUser(u.id)} className="bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </div>

      <UserForm open={addOpen} onOpenChange={setAddOpen} mode="add" onSubmit={createUser} />
      <UserForm open={!!editUser} onOpenChange={(v) => { if (!v) setEditUser(null); }} mode="edit"
        initial={editUser ? { name: editUser.name, email: editUser.email, password: "", role: editUser.role } : EMPTY}
        onSubmit={saveUser} />
    </div>
  );
}
